import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { Calendar, User, ArrowRight, X } from "lucide-react";
import { saveProfile } from "@/lib/userProfile";

const SEX_OPTIONS = [
  { key: "female", testId: "sex-female" },
  { key: "male", testId: "sex-male" },
  { key: "other", testId: "sex-other" },
];

export default function PersonalDataModal({
  open,
  initial,
  onClose,
  onSaved,
}) {
  const { t } = useLang();
  const [age, setAge] = useState(initial?.age ?? "");
  const [sex, setSex] = useState(initial?.sex ?? "");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setAge(initial?.age ?? "");
      setSex(initial?.sex ?? "");
      setError("");
    }
  }, [open, initial]);

  const submit = (e) => {
    e?.preventDefault();
    const n = parseInt(age, 10);
    if (!Number.isFinite(n)) {
      setError(t.profileModal.ageInvalid);
      return;
    }
    if (n < 15 || n > 100) {
      setError(t.profileModal.ageInvalid);
      return;
    }
    const profile = { age: n, sex: sex || null, savedAt: new Date().toISOString() };
    saveProfile(profile);
    onSaved?.(profile);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="overflow-hidden rounded-[28px] border-zinc-200 p-0 sm:max-w-[480px]"
        data-testid="profile-modal"
      >
        <DialogTitle className="sr-only">{t.profileModal.title}</DialogTitle>
        <DialogDescription className="sr-only">{t.profileModal.subtitle}</DialogDescription>

        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-200 bg-white/80 text-zinc-500 backdrop-blur transition hover:text-zinc-900"
          aria-label="Close"
          data-testid="profile-modal-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div
          className="relative overflow-hidden px-7 pt-10 pb-6"
          style={{
            background:
              "radial-gradient(420px 200px at 80% -10%, rgba(196,192,245,0.55), transparent), radial-gradient(380px 200px at 0% 110%, rgba(81,75,186,0.15), transparent)",
          }}
        >
          <motion.div
            initial={{ scale: 0.85, rotate: -8, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 14 }}
            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[color:var(--sentinel)] text-white shadow-[0_18px_40px_-20px_rgba(81,75,186,0.6)]"
          >
            <Calendar className="h-5 w-5" />
          </motion.div>
          <h3 className="mt-5 text-2xl text-zinc-900 md:text-3xl">
            {t.profileModal.title}
          </h3>
          <p className="mt-2 text-sm text-zinc-600">{t.profileModal.subtitle}</p>
        </div>

        <form onSubmit={submit} className="space-y-5 px-7 py-6" data-testid="profile-form">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
              {t.profileModal.ageLabel}
            </span>
            <input
              type="number"
              inputMode="numeric"
              min={15}
              max={100}
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="35"
              className="mt-2 w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-base text-zinc-900 outline-none transition focus:border-[color:var(--sentinel)]"
              data-testid="profile-age-input"
              autoFocus
            />
          </label>

          <fieldset>
            <legend className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
              {t.profileModal.sexLabel}
            </legend>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {SEX_OPTIONS.map((s) => {
                const active = sex === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSex(s.key)}
                    data-testid={`profile-${s.testId}`}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-2xl border px-3 py-2.5 text-sm font-medium transition ${
                      active
                        ? "border-[color:var(--sentinel)] bg-[color:var(--sentinel-tint)] text-[color:var(--sentinel-darker)]"
                        : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"
                    }`}
                  >
                    <User className="h-3.5 w-3.5" />
                    {t.profileModal[`sex${s.key.charAt(0).toUpperCase() + s.key.slice(1)}`]}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {error && (
            <p className="text-xs font-medium text-red-600" data-testid="profile-error">
              {error}
            </p>
          )}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost justify-center"
              data-testid="profile-skip"
            >
              {t.profileModal.skip}
            </button>
            <button type="submit" className="btn-primary justify-center" data-testid="profile-save">
              {t.profileModal.save}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <p className="text-[11px] text-zinc-400">{t.profileModal.privacy}</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
